/**
 * AdminSpecialtyServices — NECS admin cockpit for community-level
 * property service contracts.
 *
 * Mirrors the AdminBulkPickup per-property policy editor for EVERY
 * specialty service (Pressure Washing, Dumpster Treatment, Window
 * Cleaning, Bulk Removal, Eco-Friendly Amenities Cleaning),
 * so NECS admins can fill contract-specific details (frequency, fees,
 * scope, exclusions, tenant-facing notes) and schedule incoming PM
 * requests per service type.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Droplets, Wind, Sparkles, Recycle, Boxes, Calendar,
  ClipboardList, Users, Wrench, Save, CheckCircle2, ArrowRightLeft,
  MapPin, FileCheck, Brush,
} from 'lucide-react';
import { toast } from 'sonner';
import { RouteTaskDialog } from './admin/DeptTaskShared';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQ_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (every 3 months)' },
  { value: 'biannual', label: 'Bi-annual (twice a year)' },
  { value: 'annual', label: 'Annual (once a year)' },
  { value: 'on_request', label: 'On request only' },
];

const DAY_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const STATUS_TONE = {
  pending: 'bg-zinc-600',
  submitted: 'bg-zinc-600',
  scheduled: 'bg-amber-600 text-black',
  in_progress: 'bg-blue-600',
  completed: 'bg-emerald-600',
  cancelled: 'bg-red-700',
};

/**
 * Per-service definition: icon, tagline, contract-relevant field labels,
 * default policy content, prep checklist, and crew guidance. This is the
 * ONLY place NECS-specific service knowledge lives, so extending the
 * service catalogue is a one-line addition here.
 */
const SERVICE_CATALOG = [
  {
    key: 'pressure_washing',
    label: 'Pressure Washing',
    icon: Droplets,
    tagline: 'Exterior surfaces, sidewalks, breezeways & dumpster pads',
    accent: '#0ea5e9',
    labels: {
      scope: 'Surfaces included in the contract',
      excluded: 'Surfaces excluded / restrictions',
      max_units: 'Max square-feet per visit (0 = unlimited)',
      fee: 'Additional per-visit fee ($)',
      instructions: 'Tenant-facing instructions',
      units_placeholder: 'e.g., 3500',
    },
    defaults: {
      frequency: 'annual',
      day_of_week: 4,
      time_window: '8 AM - 12 PM',
      included_scope: 'Sidewalks, entryways, breezeways, garage floors, dumpster pads & compactor surrounds.',
      excluded_scope: 'Painted facades, wood decks, or any surface not rated for 3,000+ PSI.',
      max_units: 5000,
      additional_fee: 0,
      notice_required_hours: 48,
      instructions: 'Please move potted plants, patio furniture, and vehicles from covered surfaces the night before.',
    },
    prep_checklist: [
      'Confirm on-site water spigot & PSI-rated hose bib',
      'Notify residents 24h prior — cars, patio items to be moved',
      'Verify runoff drainage path (municipal storm-drain rules)',
    ],
    crew_notes: 'Send crew with 3,000+ PSI hot-water unit, surface cleaner attachment, biodegradable degreaser.',
  },
  {
    key: 'dumpster_treatment',
    label: 'Dumpster Treatment',
    icon: Sparkles,
    tagline: 'Deodorize, sanitize & pest-treat trash enclosures',
    accent: '#f59e0b',
    labels: {
      scope: 'Treatment scope (products & areas)',
      excluded: 'Prohibited chemicals / allergen notes',
      max_units: 'Number of dumpsters / compactors',
      fee: 'Additional per-visit fee ($)',
      instructions: 'Tenant-facing instructions',
      units_placeholder: 'e.g., 4',
    },
    defaults: {
      frequency: 'quarterly',
      day_of_week: 2,
      time_window: '9 AM - 11 AM',
      included_scope: 'Interior & exterior dumpster wash, enzyme deodorizer, food-safe sanitizer, IPM-grade insect barrier.',
      excluded_scope: 'No bleach in food-court dumpsters. No chlorinated cleaners near organic-waste bins.',
      max_units: 4,
      additional_fee: 0,
      notice_required_hours: 72,
      instructions: 'Please coordinate haul-off so the dumpster is empty on the scheduled morning.',
    },
    prep_checklist: [
      'Coordinate haul-off date so dumpster is empty on arrival',
      'Confirm HOA / municipal chemical approval (green formula on request)',
      'Post 2h "wet floor" signage after treatment',
    ],
    crew_notes: 'Bring commercial enzyme deodorizer, food-safe sanitizer, and IPM-grade insect barrier only.',
  },
  {
    key: 'window_cleaning',
    label: 'Window Cleaning',
    icon: Wind,
    tagline: 'Interior & exterior common-area window detailing',
    accent: '#22c55e',
    labels: {
      scope: 'Windows / glass included',
      excluded: 'Windows excluded / access restrictions',
      max_units: 'Total pane count',
      fee: 'Additional per-visit fee ($)',
      instructions: 'Tenant-facing instructions',
      units_placeholder: 'e.g., 120',
    },
    defaults: {
      frequency: 'biannual',
      day_of_week: 3,
      time_window: '10 AM - 2 PM',
      included_scope: 'Leasing-office glass, clubhouse windows, fitness-center panes, common-area breezeway windows (interior + exterior).',
      excluded_scope: 'Resident unit windows unless individually contracted. No tinted glass with ammonia-based cleaners.',
      max_units: 150,
      additional_fee: 0,
      notice_required_hours: 48,
      instructions: 'Please schedule around leasing office hours; screens may be temporarily removed for exterior wash.',
    },
    prep_checklist: [
      'Confirm ladder / lift access for 2nd-story panes',
      'Coordinate with leasing office hours (silent-hours protocol)',
      'Verify screens can be removed for exterior wash',
    ],
    crew_notes: 'Deionized-water fed pole + microfiber squeegee. No ammonia within 20 ft of tinted glass.',
  },
  {
    key: 'bulk_removal',
    label: 'Bulk Removal',
    icon: Boxes,
    tagline: 'Bulky-item pickup: furniture, appliances, mattresses, e-waste',
    accent: '#a855f7',
    labels: {
      scope: 'Items included in the contract',
      excluded: 'Items excluded / prohibited',
      max_units: 'Items serviced per visit (0 = unlimited)',
      fee: 'Additional per-visit fee ($)',
      instructions: 'Tenant-facing instructions',
      units_placeholder: 'e.g., 25',
    },
    defaults: {
      frequency: 'weekly',
      day_of_week: 5,
      time_window: '8 AM - 12 PM',
      included_scope: 'Furniture (couches, chairs, tables), mattresses, box springs, appliances < 300 lbs, e-waste (TVs, monitors, small electronics), rugs, holiday trees (seasonal).',
      excluded_scope: 'Construction debris, hazardous materials (paint, solvents, batteries beyond household), tires, medical waste, and any item over 300 lbs.',
      max_units: 25,
      additional_fee: 0,
      notice_required_hours: 24,
      instructions: 'Place items at the designated staging area no later than 6 AM day-of. Bag or tape all loose parts; tag e-waste for the recycling stream.',
    },
    prep_checklist: [
      'Confirm staging area location + curb-cut clearance for truck',
      'Notify residents 24h prior — cutoff time + item eligibility',
      'Verify recycling / e-waste diversion partner for the region',
    ],
    crew_notes: 'Roll-off truck + 2-person lift crew. Weight belts required for anything >100 lbs. Photograph pre-load state for the completion report.',
  },
  {
    key: 'eco_friendly_amenities',
    label: 'Eco-friendly Amenities Cleaning',
    icon: Recycle,
    tagline: 'Green-certified cleaning for pool decks, gyms, clubhouses & pet spas',
    accent: '#10b981',
    labels: {
      scope: 'Amenities included in the contract',
      excluded: 'Restrictions / allergen or chemical bans',
      max_units: 'Amenities serviced per visit',
      fee: 'Additional per-visit fee ($)',
      instructions: 'Tenant-facing instructions',
      units_placeholder: 'e.g., 4',
    },
    defaults: {
      frequency: 'weekly',
      day_of_week: 1,
      time_window: '7 AM - 10 AM',
      included_scope: 'Pool deck, cabana surfaces, fitness equipment (gym-safe disinfectant), clubhouse, pet-spa deep clean with Green-Seal / EPA Safer-Choice products only.',
      excluded_scope: 'No bleach in pet-spa. No chlorinated cleaners on pool deck (acid-wash excluded).',
      max_units: 4,
      additional_fee: 0,
      notice_required_hours: 24,
      instructions: 'Please close listed amenity for 30 minutes after cleaning; SDS available on request.',
    },
    prep_checklist: [
      'List any resident chemical allergies or fragrance restrictions',
      'Confirm pool-deck acid-wash exclusion window',
      'Verify amenity closure signage & re-open time',
    ],
    crew_notes: 'Green-Seal & EPA Safer-Choice products only. No bleach in pet-spa. Log SDS in daily report.',
  },
  // ─── Iter-131 · Commercial floor-care catalog ────────────────────────
  // Linked to the 9 General Commercial business types so the Ops
  // Command Center + Commercial Quote Engine can wire them into any
  // signed commercial contract. `business_scope: 'commercial'` also
  // hides them from the Multifamily PM services request list.
  {
    key: 'floor_care', label: 'Floor Care', icon: Sparkles, accent: '#38bdf8',
    tagline: 'Routine floor cleaning programs matched to every surface type — lobbies to service corridors',
    business_scope: 'commercial',
    business_types: ['office', 'retail', 'medical', 'hospitality', 'education', 'industrial', 'warehouse', 'mixed_use', 'general_commercial'],
    labels: { scope: 'Areas + surface types covered', excluded: 'Excluded surfaces', max_units: 'Zones serviced per visit', fee: 'Per-visit fee ($)', instructions: 'Client + occupant instructions', units_placeholder: 'e.g., 6' },
    defaults: {
      frequency: 'weekly', day_of_week: 1, time_window: '6 AM – 10 AM',
      included_scope: 'Lobbies, corridors, breakrooms, service hallways — dust-mop, damp-mop, spot buff. Surfaces: LVT, VCT, tile, sealed concrete, terrazzo.',
      excluded_scope: 'No stripping, no restorative burnishing, no polishing (see Floor Polishing / Restoration).',
      max_units: 6, additional_fee: 0, notice_required_hours: 12,
      instructions: 'Wet-floor signs staged during and 30 min after service. Bio-based cleaners only near medical/food areas.',
    },
    prep_checklist: [
      'Confirm surface types + finish per zone',
      'Verify amenity closure/reopen windows with client',
      'Stage wet-floor signage + cones',
    ],
    crew_notes: 'Auto-scrubber for open zones, microfiber flat-mops for detail. Photograph before/after per zone. Log any surface anomalies for restoration referral.',
  },
  {
    key: 'floor_polishing', label: 'Floor Polishing', icon: Sparkles, accent: '#eab308',
    tagline: 'Professional polishing that restores shine, protects surfaces and keeps floors presentation-ready',
    business_scope: 'commercial',
    business_types: ['office', 'retail', 'medical', 'hospitality', 'education', 'industrial', 'warehouse', 'mixed_use', 'general_commercial'],
    labels: { scope: 'Areas + finish level', excluded: 'Excluded surfaces', max_units: 'Sq. ft. or zones per visit', fee: 'Per-visit / per-sq-ft fee ($)', instructions: 'Access + cure-time instructions', units_placeholder: 'e.g., 4000 sq ft' },
    defaults: {
      frequency: 'monthly', day_of_week: 6, time_window: '9 PM – 4 AM',
      included_scope: 'High-speed burnishing on VCT/LVT, spray-buff, protective finish top-coat (2 coats), polished-concrete guard.',
      excluded_scope: 'No strip-and-wax (see Floor Restoration). No grout treatment.',
      max_units: 4000, additional_fee: 0, notice_required_hours: 24,
      instructions: 'Zones closed during service and 2h cure time after; access via loading dock preferred.',
    },
    prep_checklist: [
      'Vacuum + dust-mop 100% coverage first',
      'Verify finish spec sheet with client (matte / satin / gloss)',
      'Confirm HVAC on for cure',
    ],
    crew_notes: 'High-speed burnisher + propane machine only in vented zones. Ventilate 30 min post-burnish. Photograph shine level for weekly report.',
  },
  {
    key: 'floor_restoration', label: 'Floor Restoration', icon: Wrench, accent: '#f97316',
    tagline: 'Deep restoration that brings worn floors back to a professional standard and extends their life',
    business_scope: 'commercial',
    business_types: ['office', 'retail', 'medical', 'hospitality', 'education', 'industrial', 'warehouse', 'mixed_use', 'general_commercial'],
    labels: { scope: 'Areas + restoration level', excluded: 'Excluded surfaces / conditions', max_units: 'Sq. ft. per project', fee: 'Per-sq-ft fee ($)', instructions: 'Access + closure windows', units_placeholder: 'e.g., 2000 sq ft' },
    defaults: {
      frequency: 'annual', day_of_week: 6, time_window: 'After hours · overnight',
      included_scope: 'Full strip of old finish, deep-scrub grout, machine-polish or diamond-grind concrete, 4-coat protective seal, edge & corner detail.',
      excluded_scope: 'No structural repair, no tile replacement, no epoxy overlay (referral only).',
      max_units: 2000, additional_fee: 0, notice_required_hours: 72,
      instructions: 'Areas closed a minimum 12h post-restoration for cure. Client sign-off after every phase.',
    },
    prep_checklist: [
      'Site survey + photo baseline',
      'Confirm floor material + prior finish history',
      'Schedule closure windows with client 72h out',
      'Verify SDS + venting for stripper',
    ],
    crew_notes: 'Certified restoration crew (3-person). Dust barriers for adjacent occupied zones. Photograph every phase. Coordinate with Property Manager after each cure.',
  },
  {
    key: 'day_porter', label: 'Day Porter', icon: ClipboardList, accent: '#06b6d4',
    tagline: 'On-site NECS presence during business hours — spot-clean, restock, quick-response support',
    business_scope: 'commercial',
    business_types: ['office', 'retail', 'medical', 'hospitality', 'education', 'industrial', 'warehouse', 'mixed_use', 'general_commercial'],
    labels: { scope: 'On-site duties covered', excluded: 'Not included (out of scope)', max_units: 'Hours per shift', fee: 'Hourly / per-shift fee ($)', instructions: 'Client + occupant instructions', units_placeholder: 'e.g., 8 hours' },
    defaults: {
      frequency: 'daily', day_of_week: 1, time_window: '8 AM – 5 PM',
      included_scope: 'Restroom spot-clean + restock, lobby + entryway policing, spill response, trash liner change, front-of-house glass touch-ups, coffee-station tidy, meeting-room reset, minor stock replenishment.',
      excluded_scope: 'No deep cleaning, no floor stripping, no chemical treatments (see Floor Care / Restoration).',
      max_units: 8, additional_fee: 0, notice_required_hours: 24,
      instructions: 'Uniformed NECS porter checks in with property mgmt at start of shift. Daily log book left at front desk each evening.',
    },
    prep_checklist: [
      'Confirm shift start/end + break schedule with client',
      'Verify uniform + badge protocol',
      'Stage supply cart location',
      'Confirm end-of-shift log book location',
    ],
    crew_notes: '1-person coverage per site. Client-branded uniform allowed on request. Log every response ticket + photo evidence in daily report.',
  },
  {
    // Master Addendum · Carpet Cleaning and Extraction — added to
    // Operations Command Center Specialty Services list.
    key: 'carpet_cleaning_extraction', label: 'Carpet Cleaning & Extraction', icon: Brush, accent: '#9333ea',
    tagline: 'Deep carpet cleaning + hot-water extraction for common areas, offices, and lobbies',
    business_scope: 'both',
    business_types: ['office', 'retail', 'medical', 'hospitality', 'education', 'residential', 'multifamily', 'mixed_use', 'general_commercial'],
    labels: { scope: 'Areas covered', excluded: 'Not included', max_units: 'Square footage', fee: 'Per-sqft fee ($)', instructions: 'Access + prep instructions', units_placeholder: 'e.g., 8,500 sqft' },
    defaults: {
      frequency: 'quarterly', day_of_week: 2, time_window: '7 AM – 2 PM',
      included_scope: 'Pre-vacuum, spot pre-treat (traffic lanes + spills), hot-water extraction, groom, air-mover dry, edge-detail, spot-safe deodorizer.',
      excluded_scope: 'No upholstery cleaning, no rug bindings, no water-damage restoration (see Floor Restoration).',
      max_units: 8500, additional_fee: 0, notice_required_hours: 48,
      instructions: 'Clear all light furniture 24h prior. Confirm HVAC + power access for extraction rig.',
    },
    prep_checklist: [
      'Confirm square footage + carpet fiber type',
      'Photograph pre-service condition + high-traffic lanes',
      'Verify HVAC / power outlets for extraction rig',
      'Stage caution signage + air movers',
    ],
    crew_notes: '2-person carpet crew. IICRC-trained extraction technician on lead. Dry-time expectation 4-8h posted to Property Manager.',
  },
];

const emptyPolicy = (service) => ({
  enabled: false,
  frequency: service.defaults.frequency,
  pickup_day_of_month: 1,
  day_of_week: service.defaults.day_of_week,
  time_window: service.defaults.time_window,
  included_scope: service.defaults.included_scope,
  excluded_scope: service.defaults.excluded_scope,
  max_units: service.defaults.max_units,
  additional_fee: service.defaults.additional_fee,
  notice_required_hours: service.defaults.notice_required_hours,
  instructions: service.defaults.instructions,
});

export default function AdminSpecialtyServices({ initialType }) {
  const initial = SERVICE_CATALOG.find((s) => s.key === initialType) ? initialType : 'pressure_washing';
  const [activeKey, setActiveKey] = useState(initial);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  // Extra state for the crew picker (potential improvement) — we fetch
  // the workforce roster once and filter by the selected request's
  // region so admins only see agents that can actually service the job.
  const [employees, setEmployees] = useState([]);
  const [schedule, setSchedule] = useState({ open: false, request: null, date: '', assigned_agents: [] });
  const [routeCtx, setRouteCtx] = useState(null);

  const service = useMemo(
    () => SERVICE_CATALOG.find((s) => s.key === activeKey) || SERVICE_CATALOG[0],
    [activeKey],
  );

  // Index properties by id so the Schedule dialog and Reports section
  // can enrich a request with the full address / region / state without
  // a second network call.
  const propertiesById = useMemo(() => {
    const map = {};
    (properties || []).forEach((p) => { map[p.property_id] = p; });
    return map;
  }, [properties]);

  // Derive US state from a region_code that follows the R##-XX pattern
  // (e.g., R07-TX → TX). Falls back to TX since NECS is Dallas-HQ'd.
  const inferState = (regionCode) => {
    if (!regionCode) return 'TX';
    const m = /R\d{2}-([A-Z]{2})/.exec(String(regionCode).toUpperCase());
    return m ? m[1] : 'TX';
  };

  useEffect(() => {
    if (initialType && SERVICE_CATALOG.some((s) => s.key === initialType)) {
      setActiveKey(initialType);
    }
  }, [initialType]);

  const loadProperties = async () => {
    try {
      const res = await fetch(`${API}/api/properties`, { credentials: 'include' });
      if (res.ok) {
        const list = await res.json();
        setProperties(list);
        if (!propertyId && list.length) setPropertyId(list[0].property_id);
      }
    } catch (_) { /* noop */ }
  };

  const loadPolicy = async () => {
    if (!propertyId) return;
    try {
      const res = await fetch(`${API}/api/ops/properties/${propertyId}/service-policy/${service.key}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const existing = data.policy || {};
        setPolicy({ ...emptyPolicy(service), ...existing, enabled: !!existing.enabled });
      } else {
        setPolicy(emptyPolicy(service));
      }
    } catch (_) {
      setPolicy(emptyPolicy(service));
    }
  };

  const loadRequests = async () => {
    try {
      const res = await fetch(`${API}/api/service-requests`, { credentials: 'include' });
      if (res.ok) setRequests(await res.json());
    } catch (_) { /* noop */ }
  };

  const loadEmployees = async () => {
    // Powers the "Pick Crew" section in the Schedule dialog. We hit the
    // canonical /company/employees endpoint (not /employees which 404s).
    try {
      const res = await fetch(`${API}/api/company/employees`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmployees(Array.isArray(data) ? data : (data.employees || []));
      }
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadProperties(); loadRequests(); loadEmployees(); }, []);
  useEffect(() => { loadPolicy(); }, [propertyId, service.key]);

  const save = async () => {
    if (!propertyId) return toast.error('Select a property first.');
    if (!policy) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ops/properties/${propertyId}/service-policy/${service.key}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(policy),
      });
      const data = await res.json();
      if (!res.ok) toast.error(data.detail || 'Save failed');
      else toast.success(policy.enabled ? `${service.label} enabled — PM & residents notified.` : `${service.label} policy saved.`);
    } catch (_) {
      toast.error('Network error');
    }
    setSaving(false);
  };

  const setStatus = async (requestId, status, extra = null) => {
    try {
      const res = await fetch(`${API}/api/service-requests/${requestId}?status=${encodeURIComponent(status)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: extra ? JSON.stringify(extra) : undefined,
      });
      if (res.ok) {
        toast.success(`Marked ${status.replace('_', ' ')}`);
        loadRequests();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to update');
      }
    } catch (_) { toast.error('Network error'); }
  };

  const confirmSchedule = async () => {
    if (!schedule.date) return toast.error('Pick a date');
    // Persist the crew and scheduled date so the Reports Branch has
    // real "Assigned Agents" entries the moment the job is scheduled —
    // no more manual entry after the fact.
    const prop = propertiesById[schedule.request.property_id] || {};
    // Build agent name list — the Reports Branch already handles both
    // string names and rich objects, but names read better in the table.
    const agentNames = (schedule.assigned_agents || []).map((uid) => {
      const emp = employees.find((e) => e.user_id === uid);
      return emp ? (emp.name || emp.badge_number || uid) : uid;
    });
    await setStatus(schedule.request.request_id, 'scheduled', {
      assigned_agents: agentNames,
      scheduled_date: schedule.date,
      region_code: policy?.region_code || prop.region_code || null,
    });
    // Section 9 (iter-135) — Floor Restoration auto-confirm + timeline PDF
    // email. Fires the moment the Ops schedule is locked in so the PM
    // receives the phase-by-phase timeline they can forward to tenants.
    if (schedule.request.request_type === 'floor_restoration') {
      try {
        const r = await fetch(`${API}/api/service-requests/${schedule.request.request_id}/confirm-floor-restoration`, {
          method: 'POST', credentials: 'include',
        });
        if (r.ok) {
          const j = await r.json();
          if (j.emailed_to) toast.success(`Timeline PDF emailed to ${j.emailed_to}`);
          else toast.success('Floor Restoration confirmed');
        }
      } catch (_) { /* best-effort */ }
    }
    setSchedule({ open: false, request: null, date: '', assigned_agents: [] });
  };

  const activeRequests = useMemo(
    () => requests.filter((r) => r.request_type === service.key && r.status !== 'completed' && r.status !== 'cancelled'),
    [requests, service.key],
  );

  // Completed reports for this service — the "Reports Branch" every
  // Ops manager needs after an assignment closes: date, property,
  // assigned crew, and closure notes.
  const completedReports = useMemo(
    () => requests
      .filter((r) => r.request_type === service.key && r.status === 'completed')
      .sort((a, b) => new Date(b.completed_at || b.updated_at || b.created_at) - new Date(a.completed_at || a.updated_at || a.created_at)),
    [requests, service.key],
  );

  const ServiceIcon = service.icon;

  return (
    <div className="space-y-4" data-testid="admin-specialty-services">
      {/* Service switcher */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Wrench className="h-6 w-6 text-[#FFD60A]" /> PROPERTY SPECIALTY SERVICES — PER-PROPERTY CONTRACT EDITOR
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Enable a service after the contract is signed. Save contract-specific frequency, scope,
          exclusions, fees, and tenant-facing instructions per property. Enabling a service
          notifies the Property Manager and tenants automatically.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mt-4" data-testid="specialty-service-switcher">
          {SERVICE_CATALOG.map((s) => {
            const Icon = s.icon;
            const selected = activeKey === s.key;
            const openCount = requests.filter((r) => r.request_type === s.key && r.status !== 'completed' && r.status !== 'cancelled').length;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveKey(s.key)}
                aria-pressed={selected}
                data-testid={`specialty-tab-${s.key}`}
                className={`text-left p-3 rounded-lg border-2 transition ${
                  selected
                    ? 'bg-zinc-800 border-[#FFD60A] ring-2 ring-[#FFD60A]/30'
                    : 'bg-zinc-800/40 border-zinc-700 hover:border-zinc-500'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded" style={{ backgroundColor: `${s.accent}22`, color: s.accent }}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-xs font-bold text-white leading-tight">{s.label}</span>
                  {openCount > 0 && (
                    <Badge className="ml-auto bg-[#FFD60A] text-black text-[10px]">{openCount}</Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Policy editor */}
      <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6" data-testid={`specialty-policy-${service.key}`}>
        <div className="flex items-start gap-3 mb-4">
          <span className="p-2 rounded-lg" style={{ backgroundColor: `${service.accent}22`, color: service.accent }}>
            <ServiceIcon className="h-6 w-6" />
          </span>
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {service.label.toUpperCase()} — PER-PROPERTY POLICY
              {service.business_scope === 'commercial' && (
                <span
                  data-testid={`specialty-${service.key}-scope-badge`}
                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border"
                  style={{ borderColor: service.accent, color: service.accent, backgroundColor: `${service.accent}12` }}>
                  Commercial · 9 business types
                </span>
              )}
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5">{service.tagline}</p>
            {service.business_scope === 'commercial' && Array.isArray(service.business_types) && (
              <p className="text-[11px] text-zinc-500 mt-1">
                Applies to: {service.business_types.map((t) => t.replace(/_/g, ' ')).join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Property + enable */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="text-zinc-300 mb-1 block">Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`specialty-${service.key}-property-select`}>
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.length === 0 ? (
                  <SelectItem value="__none__" disabled>No properties available</SelectItem>
                ) : properties.map((p) => (
                  <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Selected property details — Address, City, State, and
                Region live directly under the name so the admin can
                confirm they picked the right building before saving any
                policy. Region auto-populates from the property record
                (seeded from the DFW regions catalog on registration). */}
            {(() => {
              const sel = propertiesById[propertyId];
              if (!sel) return null;
              const state = sel.state || inferState(sel.region_code);
              const region = sel.region_name || sel.region_code || '—';
              return (
                <div className="mt-2 bg-zinc-950/70 border border-zinc-700 rounded-md p-2 text-xs space-y-0.5"
                     data-testid={`specialty-${service.key}-selected-property-details`}>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-[#C9A962]" />
                    <span className="text-zinc-300">
                      {sel.address || <span className="italic text-zinc-500">— no address on file —</span>}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pl-4 text-[11px] text-zinc-400">
                    <span>
                      <span className="uppercase tracking-widest mr-1 text-zinc-500">City</span>
                      {sel.city || '—'}
                    </span>
                    <span>
                      <span className="uppercase tracking-widest mr-1 text-zinc-500">State</span>
                      {state}
                    </span>
                    <span>
                      <span className="uppercase tracking-widest mr-1 text-zinc-500">Region</span>
                      <span className="text-[#C9A962] font-semibold">{region}</span>
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch
              checked={!!policy?.enabled}
              onCheckedChange={(v) => setPolicy((p) => ({ ...(p || emptyPolicy(service)), enabled: v }))}
              data-testid={`specialty-${service.key}-enable-switch`}
            />
            <span className="text-white font-semibold">
              {policy?.enabled ? `Enabled — visible to PM & residents` : 'Disabled — hidden'}
            </span>
          </div>
        </div>

        {policy && (
          <>
            {/* Frequency + day + time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-zinc-300">Frequency</Label>
                <Select
                  value={policy.frequency}
                  onValueChange={(v) => setPolicy((p) => ({ ...p, frequency: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`specialty-${service.key}-frequency`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {policy.frequency === 'monthly' ? (
                <div>
                  <Label className="text-zinc-300">Day of month (1-28)</Label>
                  <Input
                    type="number" min={1} max={28}
                    value={policy.pickup_day_of_month || 1}
                    onChange={(e) => setPolicy((p) => ({ ...p, pickup_day_of_month: parseInt(e.target.value) || 1 }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid={`specialty-${service.key}-day-of-month`}
                  />
                </div>
              ) : policy.frequency !== 'on_request' ? (
                <div>
                  <Label className="text-zinc-300">Day of week</Label>
                  <Select value={String(policy.day_of_week ?? 0)} onValueChange={(v) => setPolicy((p) => ({ ...p, day_of_week: parseInt(v) }))}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid={`specialty-${service.key}-day-of-week`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_OF_WEEK.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : <div />}

              <div>
                <Label className="text-zinc-300">Time window</Label>
                <Input
                  value={policy.time_window || ''}
                  onChange={(e) => setPolicy((p) => ({ ...p, time_window: e.target.value }))}
                  placeholder="e.g., 8 AM - 12 PM"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid={`specialty-${service.key}-time-window`}
                />
              </div>
            </div>

            {/* Notice / units / fee */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-zinc-300">Notice required (hours)</Label>
                <Input
                  type="number" min={0}
                  value={policy.notice_required_hours || 0}
                  onChange={(e) => setPolicy((p) => ({ ...p, notice_required_hours: parseInt(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid={`specialty-${service.key}-notice-hours`}
                />
              </div>
              <div>
                <Label className="text-zinc-300">{service.labels.max_units}</Label>
                <Input
                  type="number" min={0}
                  value={policy.max_units || 0}
                  onChange={(e) => setPolicy((p) => ({ ...p, max_units: parseInt(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder={service.labels.units_placeholder}
                  data-testid={`specialty-${service.key}-max-units`}
                />
              </div>
              <div>
                <Label className="text-zinc-300">{service.labels.fee}</Label>
                <Input
                  type="number" step="0.01" min={0}
                  value={policy.additional_fee || 0}
                  onChange={(e) => setPolicy((p) => ({ ...p, additional_fee: parseFloat(e.target.value) || 0 }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid={`specialty-${service.key}-fee`}
                />
              </div>
            </div>

            {/* Scope + exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-zinc-300">{service.labels.scope}</Label>
                <Textarea rows={3}
                  value={policy.included_scope || ''}
                  onChange={(e) => setPolicy((p) => ({ ...p, included_scope: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  data-testid={`specialty-${service.key}-scope`}
                />
              </div>
              <div>
                <Label className="text-zinc-300">{service.labels.excluded}</Label>
                <Textarea rows={3}
                  value={policy.excluded_scope || ''}
                  onChange={(e) => setPolicy((p) => ({ ...p, excluded_scope: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  data-testid={`specialty-${service.key}-excluded`}
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-3">
              <Label className="text-zinc-300">{service.labels.instructions}</Label>
              <Textarea rows={2}
                value={policy.instructions || ''}
                onChange={(e) => setPolicy((p) => ({ ...p, instructions: e.target.value }))}
                placeholder="Anything the residents or PM should know before the crew arrives."
                className="bg-zinc-800 border-zinc-700 text-white text-sm"
                data-testid={`specialty-${service.key}-instructions`}
              />
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
              data-testid={`specialty-${service.key}-save-btn`}
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : `Save ${service.label} Policy & Notify`}
            </Button>
          </>
        )}

        {/* Reference info block — read-only quick guide for admins */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 pt-4 border-t border-zinc-800">
          <RefBlock icon={<ClipboardList className="h-4 w-4" />} title="Default scope of work">
            <p className="text-xs text-zinc-300 leading-relaxed">{service.defaults.included_scope}</p>
          </RefBlock>
          <RefBlock icon={<CheckCircle2 className="h-4 w-4" />} title="Prep checklist">
            <ul className="space-y-1 text-xs text-zinc-300">
              {service.prep_checklist.map((line) => (
                <li key={line} className="flex items-start gap-1.5">
                  <span className="text-emerald-400 mt-0.5">✓</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </RefBlock>
          <RefBlock icon={<Users className="h-4 w-4" />} title="Crew guidance">
            <p className="text-xs text-zinc-300 leading-relaxed">{service.crew_notes}</p>
          </RefBlock>
        </div>
      </Card>

      {/* Incoming PM requests for this service */}
      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid={`specialty-requests-${service.key}`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#FFD60A]" />
            {service.label} · Incoming PM Requests
          </h3>
          <Badge className="bg-[#FFD60A] text-black">{activeRequests.length}</Badge>
        </div>

        {activeRequests.length === 0 ? (
          <p className="text-xs text-zinc-500 py-6 text-center">
            No {service.label.toLowerCase()} requests yet.
          </p>
        ) : (
          <ul className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {activeRequests.map((r) => (
              <li
                key={r.request_id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4"
                data-testid={`specialty-row-${r.request_id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white">
                      {r.property_name || 'Property'} · {r.tenant_name}
                    </div>
                    <div className="text-[11px] text-zinc-400">
                      {new Date(r.created_at).toLocaleString()} · Priority:{' '}
                      <span className={r.priority === 'urgent' ? 'text-amber-400 font-semibold' : 'text-zinc-400'}>
                        {(r.priority || 'normal').toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-200 mt-2 whitespace-pre-wrap">{r.description}</p>
                  </div>
                  <Badge className={STATUS_TONE[r.status] || 'bg-zinc-600'}>{(r.status || 'pending').toUpperCase()}</Badge>
                </div>

                {(r.status === 'pending' || r.status === 'submitted') && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-amber-500 text-black hover:bg-amber-600"
                      onClick={() => setSchedule({ open: true, request: r, date: '', assigned_agents: [] })}
                      data-testid={`specialty-schedule-${r.request_id}`}
                    >
                      Schedule Crew
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#C9A962] text-[#C9A962] hover:bg-[#C9A962]/10"
                      onClick={() => setRouteCtx({
                        subject: `Service request escalation · ${service.label} at ${r.property_name || 'property'}`,
                        detail: `${r.description || ''}\n\nRequested by: ${r.tenant_name}\nFiled: ${new Date(r.created_at).toLocaleString()}\nPriority: ${(r.priority || 'normal').toUpperCase()}`,
                        dept: service.key === 'dumpster_treatment' ? 'safety' : 'operations',
                        related: `${r.property_name} · Request ${r.request_id}`,
                        priority: r.priority === 'urgent' ? 'urgent' : 'normal',
                      })}
                      data-testid={`specialty-route-${r.request_id}`}
                    >
                      <ArrowRightLeft className="h-3 w-3 mr-1" /> Route to…
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                      onClick={() => setStatus(r.request_id, 'cancelled')}
                      data-testid={`specialty-cancel-${r.request_id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {r.status === 'scheduled' && (
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => setStatus(r.request_id, 'completed')}
                      data-testid={`specialty-complete-${r.request_id}`}
                    >
                      Mark Completed
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Reports Branch — completed assignments for this service with
          the assigned agents so PMs, Ops, and Finance can see what
          shipped, when, and by whom. */}
      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid={`specialty-reports-${service.key}`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <FileCheck className="h-5 w-5" style={{ color: service.accent }} />
            {service.label} · Completed Reports
          </h3>
          <Badge className="bg-emerald-700 text-white">{completedReports.length}</Badge>
        </div>

        {completedReports.length === 0 ? (
          <p className="text-xs text-zinc-500 py-6 text-center" data-testid={`specialty-reports-${service.key}-empty`}>
            No completed {service.label.toLowerCase()} reports yet — they'll appear here the moment a crew closes a job.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`specialty-reports-table-${service.key}`}>
              <thead className="bg-zinc-950 text-zinc-400 uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="px-3 py-2 text-left">Property</th>
                  <th className="px-3 py-2 text-left">Region · State</th>
                  <th className="px-3 py-2 text-left">Completed</th>
                  <th className="px-3 py-2 text-left">Assigned Agents</th>
                  <th className="px-3 py-2 text-left">Closure Notes</th>
                </tr>
              </thead>
              <tbody>
                {completedReports.map((r) => {
                  const prop = propertiesById[r.property_id] || {};
                  const state = prop.state || inferState(prop.region_code);
                  const crew = (r.assigned_agents && r.assigned_agents.length > 0)
                    ? r.assigned_agents
                    : (r.crew_names && r.crew_names.length > 0 ? r.crew_names : []);
                  return (
                    <tr key={r.request_id}
                        className="border-t border-zinc-800 hover:bg-zinc-950/60"
                        data-testid={`specialty-report-row-${r.request_id}`}>
                      <td className="px-3 py-2 text-white font-semibold">
                        <div>{r.property_name || prop.name || '—'}</div>
                        <div className="text-[11px] text-zinc-500">{prop.address || ''}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-300 text-xs font-mono">
                        {(prop.region_code || '—')} · {state}
                      </td>
                      <td className="px-3 py-2 text-zinc-300 text-xs">
                        {(r.completed_at || r.updated_at || r.created_at)
                          ? new Date(r.completed_at || r.updated_at || r.created_at).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-3 py-2">
                        {crew.length === 0 ? (
                          <span className="text-[11px] text-zinc-500 italic">Crew not on file</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {crew.map((name, i) => (
                              <Badge key={i} variant="outline"
                                     className="border-[#C9A962] text-[#C9A962] font-normal">
                                <Users className="h-3 w-3 mr-1" />{typeof name === 'string' ? name : (name.name || name.badge_number || name.user_id)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-300 text-xs max-w-xs">
                        <span className="line-clamp-2">
                          {r.closure_notes || r.completion_notes || r.notes || '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Schedule dialog — professional confirmation sheet */}
      <Dialog open={schedule.open} onOpenChange={(v) => !v && setSchedule({ open: false, request: null, date: '', assigned_agents: [] })}>
        <DialogContent
          className="bg-white text-[#2D2D2D] border border-[#C9A962]/40 sm:max-w-md max-h-[85vh] overflow-y-auto p-4"
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${service.accent}22`, color: service.accent }}
              >
                <ServiceIcon className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle className="text-[#2D2D2D] text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  SCHEDULE {service.label.toUpperCase()} CREW
                </DialogTitle>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Confirm the crew arrival date. The Property Manager and residents will be notified.
                </p>
              </div>
            </div>
          </DialogHeader>
          {schedule.request && (() => {
            const prop = propertiesById[schedule.request.property_id] || {};
            const addressLine = [prop.address, prop.city].filter(Boolean).join(', ') || '— not on file —';
            const regionCode = prop.region_code || '—';
            const state = prop.state || inferState(prop.region_code);
            return (
            <div className="space-y-4 pt-2">
              {/* Property + Requester summary */}
              <div className="bg-[#FAF7EF] border border-[#C9A962]/30 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Property</p>
                    <p className="text-[#2D2D2D] font-bold">{schedule.request.property_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Requested by</p>
                    <p className="text-[#2D2D2D] font-bold">{schedule.request.tenant_name}</p>
                  </div>

                  {/* Address / Region / State — critical dispatch info
                      so the crew knows where they're going and the right
                      supervisor is notified per region. */}
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Address
                    </p>
                    <p className="text-[#2D2D2D] text-sm" data-testid="specialty-schedule-address">{addressLine}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Region</p>
                    <p className="text-[#2D2D2D] text-sm font-mono" data-testid="specialty-schedule-region">{regionCode}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">State</p>
                    <p className="text-[#2D2D2D] text-sm font-semibold" data-testid="specialty-schedule-state">{state}</p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Filed</p>
                    <p className="text-[#2D2D2D] text-sm">{new Date(schedule.request.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Priority</p>
                    <Badge className={schedule.request.priority === 'urgent' ? 'bg-amber-500 text-black' : 'bg-zinc-500 text-white'}>
                      {(schedule.request.priority || 'normal').toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div className="pt-2 border-t border-[#C9A962]/20">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Job details</p>
                  <p className="text-sm text-[#2D2D2D] whitespace-pre-wrap mt-1">{schedule.request.description}</p>
                </div>
              </div>

              {/* Crew arrival date */}
              <div>
                <Label htmlFor="specialty-sched-date" className="text-[#2D2D2D] font-semibold text-sm">
                  Crew arrival date *
                </Label>
                <Input
                  id="specialty-sched-date"
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={schedule.date}
                  onChange={(e) => setSchedule((s) => ({ ...s, date: e.target.value }))}
                  className={`mt-1 bg-white text-[#2D2D2D] border-2 ${
                    !schedule.date ? 'border-[#C9A962] ring-1 ring-[#C9A962]/40' : 'border-zinc-300'
                  }`}
                  data-testid="specialty-schedule-date-input"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  Notifications go out to the PM + residents once you confirm.
                </p>
              </div>

              {/* Assign Regional Supervisor — the Ops manager only
                  picks the SUPERVISOR who owns this region; that
                  supervisor then delegates to their agents/team from
                  their own dashboard. Keeping this list intentionally
                  small avoids the sprawl of listing every field agent
                  every time. */}
              <div data-testid="specialty-schedule-crew-picker">
                <Label className="text-[#2D2D2D] font-semibold text-sm flex items-center gap-1">
                  <Users className="h-4 w-4" /> Assign to Regional Supervisor
                </Label>
                <p className="text-[11px] text-zinc-500 mb-1.5">
                  Region {regionCode !== '—' ? <span className="font-mono text-[#C9A962]">{regionCode}</span> : 'not on file'}. Supervisor further assigns crew from their dashboard.
                </p>
                <div className="border border-zinc-300 rounded-md bg-white divide-y divide-zinc-200 max-h-32 overflow-y-auto">
                  {(() => {
                    const region = regionCode !== '—' ? regionCode : null;
                    const supervisors = employees.filter((e) => {
                      if (e.is_blocked) return false;
                      if ((e.role || '').toLowerCase() !== 'supervisor') return false;
                      // Region-scoped: if we have a region AND supervisor
                      // has region_code, they must match. Falls through
                      // when either side is missing so we never hide
                      // supervisors from an untagged property.
                      if (region && e.region_code && e.region_code !== region) return false;
                      return true;
                    });
                    if (supervisors.length === 0) {
                      return (
                        <div className="p-3 text-xs text-zinc-500 italic" data-testid="specialty-schedule-crew-empty">
                          No supervisor on file for this region — add one via HR &rarr; Employees.
                        </div>
                      );
                    }
                    return supervisors.map((e) => {
                      const uid = e.user_id;
                      const selected = (schedule.assigned_agents || []).includes(uid);
                      return (
                        <label
                          key={uid}
                          className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-zinc-50 ${
                            selected ? 'bg-[#C9A962]/10' : ''
                          }`}
                          data-testid={`specialty-schedule-crew-option-${uid}`}
                        >
                          <input
                            type="radio"
                            name="specialty-schedule-supervisor"
                            checked={selected}
                            onChange={() => {
                              // Radio semantics — one supervisor at a
                              // time is assigned by Ops; the supervisor
                              // then fans out to their crew.
                              setSchedule((s) => ({ ...s, assigned_agents: [uid] }));
                            }}
                          />
                          <span className="font-semibold text-[#2D2D2D]">{e.name || uid}</span>
                          <span className="text-zinc-500 font-mono">{e.badge_number || ''}</span>
                          <span className="ml-auto text-zinc-500 text-[10px] uppercase tracking-widest">
                            {e.region_code || 'any'}
                          </span>
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            );
          })()}
          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="outline"
              className="border-zinc-300 text-[#2D2D2D] hover:bg-zinc-100"
              onClick={() => setSchedule({ open: false, request: null, date: '', assigned_agents: [] })}
              data-testid="specialty-schedule-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSchedule}
              disabled={!schedule.date}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold disabled:opacity-40"
              data-testid="specialty-schedule-confirm-btn"
            >
              Confirm Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route-to-department dialog for escalating a specialty request */}
      <RouteTaskDialog
        open={!!routeCtx}
        onClose={() => setRouteCtx(null)}
        defaultDept={routeCtx?.dept || 'operations'}
        onSaved={() => setRouteCtx(null)}
        prefill={routeCtx || undefined}
      />
    </div>
  );
}

function RefBlock({ icon, title, children }) {
  return (
    <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-4">
      <div className="text-xs font-bold text-[#FFD60A] uppercase tracking-widest flex items-center gap-1.5 mb-2">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}
